import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { supervisorApi } from "@/lib/SupervisorApi";
import { Modal, Field, inputCls } from "./SupervisorPrimitives";

/**
 * Two-Factor Authentication management for the supervisor's own
 * account. Three modes:
 *   • view — status card + Enable/Disable button
 *   • setup — QR + manual secret + 6-digit confirmation
 *   • disable — requires password + a fresh 6-digit code
 */
export default function Security2FADialog({ totpEnabled, onClose, onChanged }) {
  const [mode, setMode] = useState("view"); // view | setup | disable
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [password, setPassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await supervisorApi.setup2fa();
      setQr(r.data.qr_png_base64);
      setSecret(r.data.secret);
      setMode("setup");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  const enable = async () => {
    if (!/^\d{6}$/.test(confirmCode)) { toast.error("6-ψήφιος κωδικός"); return; }
    setBusy(true);
    try {
      await supervisorApi.enable2fa(confirmCode);
      toast.success("Το 2FA ενεργοποιήθηκε");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Λάθος κωδικός");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await supervisorApi.disable2fa(password, disableCode);
      toast.success("Το 2FA απενεργοποιήθηκε");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Ασφάλεια — Two-Factor Authentication" onClose={onClose}>
      {mode === "view" && (
        <div className="space-y-4">
          <div className={`p-4 rounded-lg border ${totpEnabled ? "bg-emerald-950/30 border-emerald-800/50" : "bg-amber-950/30 border-amber-800/50"}`}>
            <div className={`font-bold text-sm ${totpEnabled ? "text-emerald-300" : "text-amber-300"} flex items-center gap-2`}>
              <ShieldCheck size={16} />
              {totpEnabled ? "Το 2FA είναι ενεργό" : "Το 2FA δεν είναι ενεργό"}
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              {totpEnabled
                ? "Κάθε είσοδος στο Supervisor Panel θα ζητά κωδικό από την εφαρμογή Google Authenticator."
                : "Ενεργοποίησε το 2FA για να προσθέσεις ένα επιπλέον επίπεδο ασφάλειας πέρα από το password."}
            </div>
          </div>
          <div className="text-xs text-zinc-500">
            Χρησιμοποίησε τις εφαρμογές: <b>Google Authenticator</b>, <b>Microsoft Authenticator</b>, <b>Authy</b> ή <b>1Password</b>.
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
            <button onClick={onClose} className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-bold" data-testid="sup-2fa-close">
              Κλείσιμο
            </button>
            {!totpEnabled ? (
              <button onClick={startSetup} disabled={busy} data-testid="sup-2fa-setup"
                className="h-10 px-4 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 text-white text-sm font-bold disabled:opacity-50">
                Ενεργοποίηση 2FA
              </button>
            ) : (
              <button onClick={() => setMode("disable")} data-testid="sup-2fa-disable-start"
                className="h-10 px-4 rounded-lg bg-rose-800 hover:bg-rose-700 text-white text-sm font-bold">
                Απενεργοποίηση 2FA
              </button>
            )}
          </div>
        </div>
      )}

      {mode === "setup" && (
        <div className="space-y-4">
          <div className="text-sm text-zinc-300">
            1) Άνοιξε την εφαρμογή Authenticator και σκάναρε το QR:
          </div>
          {qr && (
            <div className="flex justify-center bg-white p-4 rounded-lg" data-testid="sup-2fa-qr">
              <img src={qr} alt="TOTP QR" className="w-56 h-56" />
            </div>
          )}
          <div className="text-xs text-zinc-500">
            Εναλλακτικά πλήκτρωσε το secret χειροκίνητα:
            <div className="font-mono text-amber-400 text-xs mt-1 break-all">{secret}</div>
          </div>
          <div className="text-sm text-zinc-300 pt-2">
            2) Δώσε τον 6-ψήφιο κωδικό που εμφανίστηκε για επιβεβαίωση:
          </div>
          <input
            type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
            value={confirmCode}
            onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            data-testid="sup-2fa-confirm-code"
            className={inputCls + " h-14 text-2xl font-mono tracking-[0.5em] text-center"}
            autoFocus
          />
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
            <button onClick={() => setMode("view")} className="h-10 px-4 rounded-lg bg-zinc-800 text-zinc-300 text-sm font-bold">
              Πίσω
            </button>
            <button onClick={enable} disabled={busy || confirmCode.length !== 6} data-testid="sup-2fa-enable"
              className="h-10 px-4 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 text-white text-sm font-bold disabled:opacity-50">
              {busy ? "..." : "Ενεργοποίηση"}
            </button>
          </div>
        </div>
      )}

      {mode === "disable" && (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-rose-950/30 border border-rose-900/50 text-xs text-rose-200">
            Θα απενεργοποιήσεις το 2FA. Ο λογαριασμός θα προστατεύεται μόνο από το password.
          </div>
          <Field label="Τρέχον Password">
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="sup-2fa-disable-pw" autoFocus />
          </Field>
          <Field label="Τρέχων 6-ψήφιος κωδικός Authenticator">
            <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className={inputCls + " font-mono tracking-widest text-center"}
              data-testid="sup-2fa-disable-code" />
          </Field>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
            <button onClick={() => setMode("view")} className="h-10 px-4 rounded-lg bg-zinc-800 text-zinc-300 text-sm font-bold">
              Άκυρο
            </button>
            <button onClick={disable} disabled={busy || !password || disableCode.length !== 6} data-testid="sup-2fa-disable-confirm"
              className="h-10 px-4 rounded-lg bg-rose-700 hover:bg-rose-600 text-white text-sm font-bold disabled:opacity-50">
              {busy ? "..." : "Απενεργοποίηση"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
